import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import ResponsiveDrawer from "../../component/sidebar/sidebar.component";
import { ThemeColor } from "../../component/theme/color";
import { FontFamily } from "../../component/theme/fontFamily";
import { fontSize } from "../../component/theme/fontSize";
import { fontWeight } from "../../component/theme/fontWeight";
import { apiErrorMessage } from "../../helper/apiError";
import {
  getPayoutProgressByIntakeAPI,
  getStudentIntakesAPI,
} from "../../Redux/service/accountService";
import { TrackingStation } from "./PayoutActivityView.component";

/**
 * Format a timestamp into an unambiguous, readable string:
 * e.g. "15 Oct 2026, 6:10 PM". Non-technical users need exact dates for financial audits.
 */
export const formatDateTime = (value) => {
  if (!value) return "—";
  const date = dayjs(value);
  return date.isValid() ? date.format("DD MMM YYYY, h:mm A") : "—";
};

/**
 * Format monetary amount with appropriate currency symbol.
 * USD -> $, INR -> ₹.
 */
export const formatAmount = (amount, currency) => {
  if (amount === undefined || amount === null || amount === "") return null;
  const num = Number(amount);
  if (Number.isNaN(num)) return null;
  const symbol = currency === "USD" ? "$" : "₹";
  return (
    symbol +
    num.toLocaleString(currency === "USD" ? "en-US" : "en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

/** One run: a semester's payment 1, or 2, or 3 - each kept on its own. */
const IntakeStation = ({ group, latest, showApproved }) => {
  const lastTouched = (group.stages || [])
    .map((stage) => stage.lastAt)
    .filter(Boolean)
    .sort()
    .pop();

  return (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5 },
      mb: 2.5,
      backgroundColor: "#FFFFFF",
      border: "1px solid #E2E8F0",
      borderRadius: "16px",
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 1 }}
    >
      <Typography
        fontFamily={FontFamily.Lato}
        sx={{
          fontWeight: fontWeight.W700,
          fontSize: 17,
          color: ThemeColor.NAVYBLUE,
        }}
      >
        {group.intake}
      </Typography>

      {group.payment ? (
        <Chip
          size="small"
          label={`Payment ${group.payment}`}
          sx={{
            bgcolor: "#F1F5F9",
            color: "#475569",
            fontWeight: 700,
            fontSize: "0.7rem",
            borderRadius: "6px",
          }}
        />
      ) : null}

      {latest ? (
        <Chip
          size="small"
          label="Latest work"
          sx={{
            bgcolor: "#DBEAFE",
            color: "#1D4ED8",
            fontWeight: 600,
            fontSize: "0.7rem",
            borderRadius: "6px",
          }}
        />
      ) : null}

      <Typography
        fontFamily={FontFamily.Lato}
        sx={{ fontSize: 13, color: "#64748B" }}
      >
        {group.students} student{group.students === 1 ? "" : "s"} ·{" "}
        {group.completed} finished
        {lastTouched ? ` · last action ${formatDateTime(lastTouched)}` : ""}
      </Typography>
    </Stack>

    <TrackingStation
      stages={group.stages}
      notApproved={group.notApproved}
      approved={group.approved}
      showApproved={showApproved}
    />
    </Paper>
  );
};

export const PayoutHistoryContent = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const universityId = searchParams.get("universityId") || "";
  const organizationId = searchParams.get("organizationId") || "";
  // For an organisation: the university its record is for.
  const university = searchParams.get("university") || "";
  const universityName = searchParams.get("universityName") || "";
  const intake = searchParams.get("intake") || "";
  const entityNameFromUrl =
    searchParams.get("entityName") || searchParams.get("name") || "";

  const [groups, setGroups] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const updateParams = useCallback(
    (updates) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, val]) => {
        if (val !== undefined && val !== null && String(val).trim() !== "") {
          next.set(key, String(val).trim());
        } else {
          next.delete(key);
        }
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const fetchData = useCallback(async () => {
    if (!universityId && !organizationId) {
      setError("No university or organisation was given.");
      return;
    }
    if (organizationId && !university) {
      setError("Open this from By Organisation with a university selected.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await getPayoutProgressByIntakeAPI({
        universityId,
        organizationId,
        university,
        intake,
      });
      setGroups(res.data?.data || []);
    } catch (err) {
      setError(apiErrorMessage(err));
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [universityId, organizationId, university, intake]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * The same semester list the summary page offers.
   *
   * Built from this entity's own runs, the list changed as you filtered and
   * a semester you had just left could disappear from it. Reading the shared
   * list keeps the two pages offering the same choices.
   */
  useEffect(() => {
    let current = true;
    getStudentIntakesAPI()
      .then((res) => current && setIntakes(res.data?.data || []))
      .catch(() => current && setIntakes([]));
    return () => {
      current = false;
    };
  }, []);

  const entityTitle = useMemo(() => {
    if (entityNameFromUrl) return entityNameFromUrl;
    if (universityId) return "University Payout History";
    if (organizationId) return "Organisation Payout History";
    return "Payout History";
  }, [entityNameFromUrl, universityId, organizationId]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: "#F8FAFC", minHeight: "100vh" }}>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            aria-label="Go back"
            onClick={() => navigate(-1)}
            sx={{
              border: "1px solid #E2E8F0",
              backgroundColor: "#FFFFFF",
              "&:hover": { backgroundColor: "#F1F5F9" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Typography
            fontFamily={FontFamily.Lato}
            fontWeight={fontWeight.W700}
            fontSize={fontSize.h2.medium}
            color={ThemeColor.NAVYBLUE}
          >
            {entityTitle}
          </Typography>
        </Stack>

        <Typography
          fontFamily={FontFamily.Lato}
          fontWeight={fontWeight.W400}
          fontSize={fontSize.h4.small}
          color={ThemeColor.GREY}
          sx={{ ml: { xs: 0, sm: 7 }, mt: 0.5 }}
        >
          {organizationId
            ? `${universityName ? `At ${universityName}. ` : ""}One record per intake, most recently worked on first - each step shows the last time it was done.`
            : "Every run on record, most recently worked on first. The steps run once per payment, so a semester holds one record per payment - what was done, and when."}
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "16px",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Box sx={{ minWidth: 240 }}>
            <Typography
              fontFamily={FontFamily.Lato}
              sx={{ fontSize: 13, color: "#475569", fontWeight: 600, mb: 0.75 }}
            >
              Intake
            </Typography>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={intake}
              inputProps={{ "aria-label": "Intake" }}
              onChange={(event) => updateParams({ intake: event.target.value })}
              sx={{ borderRadius: "8px", backgroundColor: "#F8FAFC" }}
            >
              <MenuItem value="">All intakes</MenuItem>
              {intakes.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {intake ? (
            <Button
              size="small"
              startIcon={<FilterAltOffIcon />}
              onClick={() => updateParams({ intake: "" })}
              sx={{ textTransform: "none", alignSelf: { sm: "flex-end" }, mb: { sm: 0.25 } }}
            >
              Show every intake
            </Button>
          ) : null}
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            border: "1px solid #E2E8F0",
            borderRadius: "16px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Typography sx={{ color: ThemeColor.RED }}>{error}</Typography>
        </Paper>
      ) : groups.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            border: "1px solid #E2E8F0",
            borderRadius: "16px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Typography sx={{ color: ThemeColor.GREY, fontSize: 14 }}>
            {intake
              ? `Nothing recorded for ${intake}.`
              : "No payout activity recorded yet."}
          </Typography>
        </Paper>
      ) : (
        groups.map((group, index) => (
          <IntakeStation
            key={group.id}
            group={group}
            showApproved={!organizationId}
            // Only when showing everything: filtered to one semester, calling
            // it "current" would be a guess.
            latest={index === 0 && !intake}
          />
        ))
      )}
    </Box>
  );
};

export default function PayoutHistoryPage() {
  return <ResponsiveDrawer maincontent={<PayoutHistoryContent />} />;
}
